import { Store } from '@adapters/prismic/fetching/stores/types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { weeklyHours } from '@utils/store/index';
import { buildTimeZone } from '@utils/store/buildTimeZone';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export interface Hours {
  open: string;
  close: string;
}

function isClosed(hours: Hours) {
  if (hours?.open?.length === 0) return true;

  return ['closed', 'fermé', 'close', 'ferme'].includes(
    hours?.close?.trim().toLowerCase()
  );
}

function isBeforeOpen(time: string, hours: Hours) {
  let open = hours.open;
  const isPM = open?.indexOf('pm') > -1;
  const isFr = open?.indexOf('h') > -1;

  if (isFr) {
    const hhmm = open.replace(/\s/g, '').split('h');
    return time < `${hhmm[0]}:${hhmm[1] || '00'}`;
  }

  open = open?.replace('am', '').replace('pm', '').trim();
  const hhmm = open?.split(':');

  if (hhmm?.[0].length < 2) hhmm[0] = `0${hhmm[0]}`;
  if (open?.length < 5) open = `0${open}`;

  if (isPM) {
    return time < `${hhmm[0] + 12}:${hhmm[1]}`;
  } else {
    return time < open;
  }
}

function isPast3PM(time: string) {
  return time >= '15:00:00';
}

export function buildStorelessSPUHoursMessaging(t: (args0: string) => string) {
  const time = dayjs();
  const currentTime = dayjs(time).format('HH:mm:ss');

  // Open and Before 3PM
  if (!isPast3PM(currentTime)) {
    return { key: 'openTodayBeforeThreePM', day: null };
  }

  return { key: 'pastTodayOpenTomorrow', day: null };
}

export function buildSPUHoursMessaging(
  store: Store,
  t: (args0: string, args1?: { day?: string }) => string
) {
  const storeHours = weeklyHours(
    store?.data?.store_hours,
    store?.data?.store_hour_exceptions
  );

  if (!storeHours?.length) {
    console.error(
      `Store Hours missing, unable to build SPU 2hour messaging. Store: ${store?.data?.store_name}`,
      store
    );
  }

  const time = dayjs();
  const timeZone = buildTimeZone(store.data.address_province);
  const currentTime = dayjs(time).tz(`${timeZone}`).format('HH:mm:ss');

  const today = storeHours[0];
  const tomorrow = storeHours[1];
  const nextOpenDay = storeHours.find(
    (day, index) => !isClosed(day) && index !== 0
  );
  const day = t(`stores:${nextOpenDay?.day}`);

  // Open and before 12am
  if (!isClosed(today) && isBeforeOpen(currentTime, today)) {
    return { key: 'openTodayBeforeOpen', day: null } as const;
  }

  // Open and Before 3PM
  else if (!isClosed(today) && !isPast3PM(currentTime)) {
    return { key: 'openTodayBeforeThreePM', day: null } as const;
  }

  // Closed Today/Past 3PM and Open Tomorrow
  else if ((isClosed(today) || isPast3PM(currentTime)) && !isClosed(tomorrow)) {
    return { key: 'pastTodayOpenTomorrow', day: null } as const;
  }

  // Closed Today/Past 3PM and Closed Tomorrow
  else if ((isClosed(today) || isPast3PM(currentTime)) && isClosed(tomorrow)) {
    return { key: 'pastTodayClosedTomorrow', day } as const;
  }

  console.error('SPU Hours Messaging retuned an unexpected state', store);
  return { key: 'pastTodayClosedTomorrow', day } as const;
}

export function daysUntilNext(dayName: string, locale = 'en'): number {
  // Map day names to indices (Sunday = 0, Monday = 1, ..., Saturday = 6)
  const daysEn = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const daysFr = [
    'dimanche',
    'lundi',
    'mardi',
    'mercredi',
    'jeudi',
    'vendredi',
    'samedi',
  ];
  const days = locale === 'fr' ? daysFr : daysEn;

  const today = new Date();
  const todayIndex = today.getDay();
  const targetIndex = days.findIndex(
    (d) => d.toLowerCase() === dayName.toLowerCase()
  );

  if (targetIndex === -1) return -1; // Invalid day name

  let diff = (targetIndex - todayIndex + 7) % 7;
  if (diff === 0) diff = 7; // If today, return 7 (next week)

  return diff;
}
